import { useEffect, useMemo, useRef, useState } from "react";

import { ChevronDown, ChevronRight, ChevronUp } from "../../components/icons/svg/outline";
import ManagementCell from "./ManagementCells";
import {
  resolveSortValue,
  resolveValue,
  rowMatchesSearch,
} from "../../utils/collections/columnValues";
import { compareSortValues } from "../../utils/collections/sort";

/**
 * Usable floor for a column. `minWidth` wins when both are set.
 *
 * @param {Object} column - Column config.
 * @returns {string} CSS length.
 */
function columnFloor(column) {
  return column.minWidth || column.width || "0px";
}

/**
 * Convert a px or rem length to pixels. Rem uses the 16px root size.
 *
 * @param {string} value - CSS length.
 * @returns {number}
 */
function lengthToPx(value) {
  const match = String(value).trim().match(/^([\d.]+)(rem|px)$/);
  if (!match) return 0;
  const amount = Number(match[1]);
  return match[2] === "rem" ? amount * 16 : amount;
}

/**
 * Smallest table width that keeps every column at its usable floor.
 *
 * @param {Array<Object>} columns - Column configs.
 * @returns {string} CSS width.
 */
function tableMinWidth(columns) {
  return `calc(${columns.map(columnFloor).join(" + ")})`;
}

/**
 * Share of the table for each column, in proportion to its usable floor.
 * Used before the card width has been measured.
 *
 * @param {Array<Object>} columns - Column configs.
 * @returns {Array<string>} Percentage widths.
 */
function columnShares(columns) {
  const weights = columns.map((column) => lengthToPx(columnFloor(column)));
  const total = weights.reduce((sum, weight) => sum + weight, 0) || 1;
  return weights.map((weight) => `${(weight / total) * 100}%`);
}

/**
 * Upper size for a column. A missing max means the column may keep growing.
 *
 * @param {Object} column - Column config.
 * @returns {number} Pixel cap, or Infinity.
 */
function columnCap(column) {
  if (!column.maxWidth) return Infinity;
  return lengthToPx(column.maxWidth);
}

/**
 * Grow columns in proportion to their floors until each hits `maxWidth`.
 * Widths stay at the floor when the card is narrower than that sum.
 *
 * @param {Array<Object>} columns - Column configs.
 * @param {number} containerWidth - Visible card width in pixels.
 * @returns {Array<number>} Pixel width for each column.
 */
function distributeColumnWidths(columns, containerWidth) {
  const floors = columns.map((column) => lengthToPx(columnFloor(column)));
  const caps = columns.map(columnCap);
  const sumFloor = floors.reduce((sum, width) => sum + width, 0);
  if (!containerWidth || containerWidth <= sumFloor) {
    return floors;
  }

  const widths = [...floors];
  let extra = containerWidth - sumFloor;
  // Each pass fills columns that still have room, then shares what a cap rejected.
  for (let pass = 0; pass < columns.length && extra > 0.5; pass += 1) {
    const open = widths
      .map((width, index) => ({
        index,
        room: caps[index] - width,
        weight: floors[index] || 1,
      }))
      .filter((item) => item.room > 0.5);
    if (open.length === 0) break;

    const weight = open.reduce((sum, item) => sum + item.weight, 0);
    let used = 0;
    for (let itemIndex = 0; itemIndex < open.length; itemIndex += 1) {
      const item = open[itemIndex];
      const add = Math.min(extra * (item.weight / weight), item.room);
      widths[item.index] += add;
      used += add;
    }
    extra -= used;
    if (used < 0.5) break;
  }
  return widths;
}

/**
 * Build the patch object a column writes for an edited value.
 *
 * @param {Object} column - Column config.
 * @param {*} nextValue - Edited value.
 * @returns {Object} Partial row update.
 */
function buildPatch(column, nextValue) {
  if (typeof column.toPatch === "function") {
    return column.toPatch(nextValue);
  }
  return { [column.key]: nextValue };
}

/**
 * Sectioned table with inline editing.
 *
 * Rows stay inside the section whose `match` returns true, and sorting never
 * mixes sections. A section that receives a row from another section opens so
 * the move stays visible. A row added after the table loads stays where it was
 * created until the sort changes or the page reloads.
 *
 * @param {Object} props - Component props.
 * @param {Array<Object>} props.columns - Column configs (`key`, `label`, `type`, `editable`, `options`, `toPatch`).
 * @param {Array<Object>} props.rows - Saved rows.
 * @param {Array<{ id: string, label: string, match: Function, defaultExpanded?: boolean }>} props.sections - Row groups, top to bottom.
 * @param {{ key: string, direction?: "asc"|"desc" }} [props.defaultSort] - Initial sort inside each section.
 * @param {(row: Object, patch: Object) => Promise<*>} props.onCommit - Persist a saved row.
 * @param {(rowId: string|number) => boolean} [props.isRowSaving] - Whether that row's save is in flight.
 * @param {boolean} [props.includeSearch=true] - Show a search box.
 * @param {string} [props.searchPlaceholder="Search..."] - Search placeholder.
 * @param {string} [props.emptyMessage="No results."] - Message when a section has no visible rows.
 * @param {Object|null} [props.draftRow=null] - Unsaved row pinned to the top of its section.
 * @param {(patch: Object) => void} [props.onDraftChange] - Merge a patch into the draft.
 * @param {() => void} [props.onDraftFinish] - Save or discard the draft when focus leaves it.
 * @param {() => void} [props.onDraftCancel] - Discard the draft immediately.
 * @param {React.ReactNode} [props.toolbar] - Controls rendered beside the search box.
 * @returns {JSX.Element}
 */
export default function ManagementTable({
  columns,
  rows,
  sections,
  defaultSort = null,
  onCommit,
  isRowSaving,
  includeSearch = true,
  searchPlaceholder = "Search...",
  emptyMessage = "No results.",
  draftRow = null,
  onDraftChange,
  onDraftFinish,
  onDraftCancel,
  toolbar = null,
}) {
  const [sortField, setSortField] = useState(defaultSort?.key ?? null);
  const [sortDirection, setSortDirection] = useState(
    defaultSort?.direction === "desc" ? "desc" : "asc"
  );
  // Ids created after the first load. They stay put until the next sort.
  const [pinnedIds, setPinnedIds] = useState([]);
  const knownIdsRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expanded, setExpanded] = useState(() => {
    const initial = {};
    sections.forEach((section) => {
      initial[section.id] = section.defaultExpanded !== false;
    });
    return initial;
  });
  const sectionByRowRef = useRef(new Map());
  const scrollRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return undefined;
    const measure = () => setContainerWidth(node.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const columnWidths = distributeColumnWidths(columns, containerWidth);
  const assignedWidth = columnWidths.reduce((sum, width) => sum + width, 0);
  // Once every column is at its max, stop stretching the table across the card.
  const tableAtCaps = containerWidth > 0 && assignedWidth < containerWidth - 1;

  const displayRows = useMemo(
    () => (draftRow ? [draftRow, ...rows] : rows),
    [draftRow, rows]
  );

  // Pin new ids during render so the first paint already keeps them in place.
  // An effect would sort them once, then jump them back.
  const loadedIds = rows.filter((row) => !row._draft).map((row) => String(row.id));
  if (knownIdsRef.current === null) {
    knownIdsRef.current = new Set(loadedIds);
  } else {
    const added = loadedIds.filter((id) => !knownIdsRef.current.has(id));
    if (added.length) {
      added.forEach((id) => knownIdsRef.current.add(id));
      const newestFirst = added.slice().reverse();
      setPinnedIds((current) => [...new Set([...newestFirst, ...current])]);
    }
  }

  useEffect(() => {
    const previous = sectionByRowRef.current;
    const next = new Map();
    const draftWasOpen = previous.has("draft");
    const draftIsOpen = displayRows.some((row) => row._draft);
    displayRows.forEach((row) => {
      const section = sections.find((item) => item.match(row));
      const prior = previous.get(String(row.id));
      // Open the destination when a row changes section, so the move is visible.
      if (prior && section && prior !== section.id) {
        setExpanded((current) => ({ ...current, [section.id]: true }));
      }
      // A posted draft gets a new id. Open whichever section that row landed in.
      if (draftWasOpen && !draftIsOpen && !prior && section && !row._draft) {
        setExpanded((current) => ({ ...current, [section.id]: true }));
      }
      if (section) {
        next.set(String(row.id), section.id);
      }
    });
    sectionByRowRef.current = next;
  }, [displayRows, sections]);

  /**
   * Toggle sort within every section. The first click on a column sorts ascending.
   * Newly added rows rejoin the sort.
   *
   * @param {string} field - Column key.
   * @returns {void}
   */
  const handleSort = (field) => {
    setPinnedIds([]);
    if (sortField === field) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortDirection("asc");
  };

  const sortColumn = columns.find((column) => column.key === sortField);

  /**
   * Visible rows for one section. Drafts stay first, then rows added since the
   * last sort, then the current sort.
   *
   * @param {Object} section - Section config.
   * @returns {Array<Object>}
   */
  const rowsForSection = (section) => {
    const matched = displayRows.filter(
      (row) =>
        section.match(row) &&
        (row._draft || rowMatchesSearch(row, searchQuery, columns))
    );
    const drafts = matched.filter((row) => row._draft);
    const saved = matched.filter((row) => !row._draft);
    const pinnedOrder = new Map(pinnedIds.map((id, index) => [id, index]));
    const pinned = [];
    const rest = [];
    saved.forEach((row) => {
      if (pinnedOrder.has(String(row.id))) pinned.push(row);
      else rest.push(row);
    });
    pinned.sort(
      (left, right) =>
        pinnedOrder.get(String(left.id)) - pinnedOrder.get(String(right.id))
    );
    if (!sortColumn) {
      return [...drafts, ...pinned, ...rest];
    }
    const sorted = [...rest].sort((left, right) =>
      compareSortValues(
        resolveSortValue(left, sortColumn),
        resolveSortValue(right, sortColumn),
        sortDirection
      )
    );
    return [...drafts, ...pinned, ...sorted];
  };

  /**
   * Write a cell edit to the draft or to the saved row.
   *
   * @param {Object} row - Edited row.
   * @param {Object} column - Column config.
   * @param {*} nextValue - Edited value.
   * @returns {void}
   */
  const commitCell = (row, column, nextValue) => {
    const patch = buildPatch(column, nextValue);
    if (row._draft) {
      onDraftChange?.(patch);
      return;
    }
    onCommit(row, patch);
  };

  return (
    <div className="rounded-lg bg-white shadow-md shadow-gray-300">
      {includeSearch || toolbar ? (
        <div className="flex items-center gap-4 border-b border-gray-100 px-3 py-3">
          {includeSearch ? (
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder={searchPlaceholder}
              className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 py-0.5 text-sm caret-black placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:max-w-sm"
            />
          ) : (
            <span />
          )}
          {toolbar ? (
            <div className="ml-auto shrink-0 whitespace-nowrap">{toolbar}</div>
          ) : null}
        </div>
      ) : null}
      <div ref={scrollRef} className="overflow-x-auto">
        <table
          className="table-fixed border-collapse text-left"
          style={{
            width: tableAtCaps ? assignedWidth : "100%",
            minWidth: tableMinWidth(columns),
          }}
        >
          <colgroup>
            {(containerWidth > 0 ? columnWidths : columnShares(columns)).map(
              (size, index) => (
                <col key={columns[index].key} style={{ width: size }} />
              )
            )}
          </colgroup>
          <thead className="bg-gray-100 text-[11px] uppercase tracking-wide text-gray-500">
            <tr>
              {columns.map((column) => {
                const sortable = column.sortable !== false;
                const isActive = sortField === column.key;
                const align =
                  column.align === "right"
                    ? "text-right"
                    : column.align === "center"
                      ? "text-center"
                      : "text-left";
                return (
                  <th
                    key={column.key}
                    title={column.headerTitle || undefined}
                    className={`px-2 py-2 align-bottom font-semibold ${align} ${
                      sortable ? "cursor-pointer select-none hover:bg-gray-200/60" : ""
                    }`}
                    onClick={sortable ? () => handleSort(column.key) : undefined}
                  >
                    <span
                      className={`flex w-full min-w-0 items-start gap-0.5 ${
                        column.align === "right"
                          ? "justify-end"
                          : column.align === "center"
                            ? "justify-center"
                            : "justify-start"
                      }`}
                    >
                      <span className="min-w-0 whitespace-normal break-words leading-tight">
                        {column.label}
                      </span>
                      {sortable ? (
                        isActive && sortDirection === "asc" ? (
                          <ChevronUp className="h-3 w-3 flex-shrink-0 text-gray-800" />
                        ) : (
                          <ChevronDown
                            className={`h-3 w-3 flex-shrink-0 ${
                              isActive ? "text-gray-800" : "text-gray-300"
                            }`}
                          />
                        )
                      ) : null}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => {
              const sectionRows = rowsForSection(section);
              const isOpen = expanded[section.id] !== false;
              const SectionChevron = isOpen ? ChevronDown : ChevronRight;
              return (
                <SectionBlock
                  key={section.id}
                  section={section}
                  rows={sectionRows}
                  columns={columns}
                  isOpen={isOpen}
                  ChevronIcon={SectionChevron}
                  emptyMessage={emptyMessage}
                  isRowSaving={isRowSaving}
                  onToggle={() =>
                    setExpanded((current) => ({
                      ...current,
                      [section.id]: !isOpen,
                    }))
                  }
                  onCommit={commitCell}
                  onDraftFinish={onDraftFinish}
                  onDraftCancel={onDraftCancel}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * One collapsible group of rows inside the management table.
 *
 * @param {Object} props - Component props.
 * @param {Object} props.section - Section config.
 * @param {Array<Object>} props.rows - Rows in this section.
 * @param {Array<Object>} props.columns - Column configs.
 * @param {boolean} props.isOpen - Whether the rows are visible.
 * @param {React.ComponentType<{className?: string}>} props.ChevronIcon - Section chevron.
 * @param {string} props.emptyMessage - Text shown when the section is open and empty.
 * @param {(rowId: string|number) => boolean} [props.isRowSaving] - Save-in-flight check.
 * @param {() => void} props.onToggle - Collapse or expand the section.
 * @param {(row: Object, column: Object, nextValue: *) => void} props.onCommit - Cell commit.
 * @param {() => void} [props.onDraftFinish] - Leave-draft handler.
 * @param {() => void} [props.onDraftCancel] - Escape handler for the draft.
 * @returns {JSX.Element}
 */
function SectionBlock({
  section,
  rows,
  columns,
  isOpen,
  ChevronIcon,
  emptyMessage,
  isRowSaving,
  onToggle,
  onCommit,
  onDraftFinish,
  onDraftCancel,
}) {
  return (
    <>
      <tr className="bg-gray-50">
        <td colSpan={Math.max(columns.length, 1)} className="p-0">
          <button
            type="button"
            className="flex w-full items-center gap-2 px-3 py-1 text-left text-sm font-semibold text-gray-800"
            aria-expanded={isOpen}
            onClick={onToggle}
          >
            <ChevronIcon className="h-4 w-4 text-gray-500" />
            <span>{section.label}</span>
            <span className="font-normal text-gray-500">({rows.length})</span>
          </button>
        </td>
      </tr>
      {isOpen && rows.length === 0 ? (
        <tr>
          <td
            colSpan={Math.max(columns.length, 1)}
            className="px-3 py-3 text-center text-sm text-gray-500"
          >
            {emptyMessage}
          </td>
        </tr>
      ) : null}
      {isOpen
        ? rows.map((row) => (
            <tr
              key={row.id}
              className={`border-t border-gray-100 text-xs text-gray-700 hover:bg-gray-100 ${
                row._draft ? "bg-emerald-50/60" : "bg-white"
              }`}
              onBlur={
                row._draft
                  ? (event) => {
                      const rowElement = event.currentTarget;
                      const next = event.relatedTarget;
                      if (next && rowElement.contains(next)) {
                        return;
                      }
                      // relatedTarget is often null when focus moves to a control
                      // in this row. Check on the next turn before posting.
                      window.setTimeout(() => {
                        if (rowElement.contains(document.activeElement)) {
                          return;
                        }
                        onDraftFinish?.();
                      }, 0);
                    }
                  : undefined
              }
            >
              {columns.map((column) => {
                const editValue = resolveValue(row, column);
                const shown =
                  column.type === "readonly" && typeof column.getDisplay === "function"
                    ? column.getDisplay(row)
                    : editValue;
                const align =
                  column.align === "right"
                    ? "text-right"
                    : column.align === "center"
                      ? "text-center"
                      : "text-left";
                const marksFocus =
                  column.type === "text" ||
                  column.type === "select" ||
                  column.type === "number" ||
                  column.type === "longtext";
                return (
                  <td
                    key={column.key}
                    className={`overflow-hidden px-3 py-1 ${align} ${
                      marksFocus ? "focus-within:bg-gray-200" : ""
                    }`}
                  >
                    <ManagementCell
                      column={column}
                      value={shown}
                      disabled={isRowSaving?.(row.id) || false}
                      autoFocus={Boolean(row._draft && column.autoFocus)}
                      onEscape={row._draft ? onDraftCancel : undefined}
                      onCommit={(nextValue) => onCommit(row, column, nextValue)}
                    />
                  </td>
                );
              })}
            </tr>
          ))
        : null}
    </>
  );
}
